<template>
  <div ref="panel" class="ddm-panel">
    <div id="due-diligence-map">
      <MapOverlays :map="map" autoShowSpecies ref="mapOverlays" @state="overlayState = $event"/>
      <v-row class="map-button-row-top" dense justify="end">
        <div class="map-overlay-toggle-group" @mouseenter="hideMapTips">
          <Icon
          icon="zoom-fit"
          @icon-clicked="centerCamera"
          xLarge
          :small="false"
          iconColor="white"
          margin="map-option-button"
          tooltipColor="black"
          :tooltipText="$t('centerCamera')"
          elementId="dd-map-zoom"/>
          <Icon
          v-if="overlayState.species.canShow"
          icon="mdi-paw"
          @icon-clicked="toggleSpeciesLayer"
          :disabled="overlayState.topo.active"
          :tooltipText="$t('speciesLayer')"
          xLarge
          :small="false"
          :iconColor="overlayState.species.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="dd-map-species-toggle"/>
          <Icon
          icon="mdi-image-filter-hdr"
          @icon-clicked="toggleTopo"
          :disabled="overlayState.species.active"
          :tooltipText="$t('topoLayer')"
          xLarge
          :small="false"
          :iconColor="overlayState.topo.active ? 'primary' : 'grey darken-2'"
          margin="map-option-button"
          tooltipColor="black"
          elementId="dd-map-topo-toggle"/>
        </div>
      </v-row>
    </div>
    <div v-if="loading" class="ddm-loading d-flex align-center justify-center">
      <v-progress-circular indeterminate color="primary"/>
    </div>
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import { getMapWithLegendInstance } from '@/utils/map/Map'
import { angelGeojsonClient } from '@/utils/map/Geojson'

export default {
  name: 'DueDiligenceMapPanel',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    MapOverlays: () => import('@/components/maps/MapOverlays.vue')
  },

  props: {
    tractId: { type: Number, required: true },
    latitude: { type: Number, required: true },
    longitude: { type: Number, required: true }
  },

  data: () => ({
    mapWithLegend: null,
    loading: true,
    overlayState: { species: { canShow: false, active: false }, topo: { active: false } }
  }),

  computed: {
    map () {
      return this.mapWithLegend?.map
    }
  },

  async mounted () {
    const angelGeojson = await angelGeojsonClient.readGeojson(this.tractId)
    this.mapWithLegend = getMapWithLegendInstance({
      angelGeojson,
      onReady: () => {
        this.addSpotMarker()
        this.map.resize()
        this.loading = false
      },
      mapHtmlId: 'due-diligence-map',
      latitude: this.latitude,
      longitude: this.longitude,
      zoom: 14
    })
    this.observePanel()
  },

  beforeDestroy () {
    if (this.panelResizeObserver) this.panelResizeObserver.disconnect()
    this.panelResizeObserver = null
    if (this.mapWithLegend) this.mapWithLegend.dispose()
    this.mapWithLegend = null
  },

  methods: {
    addSpotMarker () {
      const marker = new atlas.HtmlMarker({
        draggable: false,
        position: [this.longitude, this.latitude],
        color: '#D15F27'
      })
      this.map.markers.add(marker)
    },

    centerCamera () {
      if (this.mapWithLegend) this.mapWithLegend.zoom()
    },

    toggleSpeciesLayer () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleSpeciesLayer()
    },

    toggleTopo () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.toggleTopo()
    },

    hideMapTips () {
      if (this.$refs.mapOverlays) this.$refs.mapOverlays.hideDataTips()
    },

    observePanel () {
      const el = this.$refs.panel
      if (!el) return
      this.panelResizeObserver = new ResizeObserver(() => {
        if (this.map) this.map.resize()
      })
      this.panelResizeObserver.observe(el)
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
<style scoped>
.ddm-panel {
  position: relative;
  width: 100%;
  height: calc(90vh - 200px);
  min-height: 420px;
}
#due-diligence-map {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, .2);
}
.ddm-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: rgba(255, 255, 255, 0.6);
}
</style>
